import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import PipelineStage from '../../components/shared/PipelineStage';
import { PlusIcon, XMarkIcon, UserGroupIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
// hire_date is a DATE column — never a JS Date. See lib/dateOnly.js.
import { formatDateOnlyNumeric } from '../../lib/dateOnly';
import { DATE_FLOOR } from '../../lib/dateBounds';

const roles = ['director', 'admin', 'teacher', 'float', 'cook'];

// Status filter pills, in order. "current" is the default — what most
// admins want most of the time (the working roster). Paused / past
// hidden behind one click each. "all" lifts every filter.
const STATUS_FILTERS = [
  { id: 'current',    label: 'Active + Onboarding', hint: 'Default — the working roster.' },
  { id: 'active',     label: 'Active only',         hint: 'Fully cleared, currently working.' },
  { id: 'onboarding', label: 'Onboarding',          hint: 'New hires in clearance or onboarding.' },
  { id: 'paused',     label: 'Paused',              hint: 'Temporarily off (seasonal break, leave).' },
  { id: 'past',       label: 'Past staff',          hint: 'Terminated / former employees.' },
  { id: 'all',        label: 'All',                 hint: 'Everyone in the system, all statuses.' },
];

// Role-filter pills (orthogonal to status). "any" = no filter.
const ROLE_FILTERS = [
  { id: 'any',      label: 'All roles' },
  { id: 'director', label: 'Directors' },
  { id: 'admin',    label: 'Admins' },
  { id: 'teacher',  label: 'Teachers' },
  { id: 'float',    label: 'Floats' },
  { id: 'cook',     label: 'Cooks' },
];

export default function StaffPage() {
  const { isDirector } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('current');
  const [roleFilter, setRoleFilter] = useState('any');
  const { register, handleSubmit, reset } = useForm();

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff?filter=${statusFilter}`);
      setStaff(data);
    } catch (err) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  // Supervisor dropdown in the Add Staff modal needs the CURRENT admin
  // list regardless of the active filter — otherwise viewing "Past
  // staff" and clicking Add Staff would only let you pick a past
  // supervisor. Tiny separate fetch on mount, cached.
  const [supervisorPool, setSupervisorPool] = useState([]);
  useEffect(() => {
    api.get('/staff?filter=current')
      .then(({ data }) => setSupervisorPool(data.filter(s => s.role === 'admin' || s.role === 'director')))
      .catch(() => {});
  }, []);

  // Role filtering is client-side — keeps the API simple, and the list
  // is small enough that it's not worth a round-trip.
  const visible = roleFilter === 'any'
    ? staff
    : staff.filter(s => s.role === roleFilter);

  const onSubmit = async (formData) => {
    try {
      await api.post('/auth/users', {
        ...formData,
        supervisor_id: formData.supervisor_id ? parseInt(formData.supervisor_id) : null,
      });
      toast.success('Staff member created');
      setShowForm(false);
      reset();
      loadStaff();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create user');
    }
  };

  // Use the cached supervisor pool (always-current) so the modal works
  // even when the visible filter is "Past staff" or similar.
  const leadAdmins = supervisorPool;

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Directory</h1>
          <p className="text-gray-500 text-sm">
            Showing {visible.length} of {staff.length} staff
            {statusFilter !== 'current' && ` · ${STATUS_FILTERS.find(f => f.id === statusFilter)?.label}`}
            {roleFilter !== 'any' && ` · ${ROLE_FILTERS.find(f => f.id === roleFilter)?.label}`}
          </p>
        </div>
        {isDirector && (
          <div className="flex items-center gap-2">
            <BulkWelcomeButton onSent={loadStaff} />
            <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 transition-colors font-medium shadow-lg shadow-orange-200/50">
              <PlusIcon className="w-5 h-5" /> Add Staff
            </button>
          </div>
        )}
      </div>

      {/* Status filter — primary toggle the director asked for */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {STATUS_FILTERS.map(f => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)} title={f.hint}
            className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
              statusFilter === f.id
                ? 'bg-orange-500 text-white border-orange-500'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Role filter — secondary toggle, orthogonal to status */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {ROLE_FILTERS.map(f => (
          <button key={f.id} onClick={() => setRoleFilter(f.id)}
            className={`text-[11px] font-medium px-2.5 py-1 rounded-md border transition-colors ${
              roleFilter === f.id
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No staff match these filters.</p>
          {(statusFilter !== 'current' || roleFilter !== 'any') && (
            <button onClick={() => { setStatusFilter('current'); setRoleFilter('any'); }}
              className="mt-3 text-xs text-orange-600 hover:text-orange-700 font-medium">
              Reset filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((s) => (
            <StaffCard key={s.id} s={s} isDirector={isDirector} onSent={loadStaff} />
          ))}
        </div>
      )}

      {/* Add Staff Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold">Add Staff Member</h2>
              <button onClick={() => { setShowForm(false); reset(); }}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input {...register('first_name', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input {...register('last_name', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" {...register('email', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input type="password" {...register('password', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input {...register('phone')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select {...register('role', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">Select role</option>
                    {roles.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supervisor</label>
                  <select {...register('supervisor_id')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">None</option>
                    {leadAdmins.map(la => <option key={la.id} value={la.id}>{la.first_name} {la.last_name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
                <input type="date" min={DATE_FLOOR} {...register('hire_date')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 transition-colors">Create Staff</button>
                <button type="button" onClick={() => { setShowForm(false); reset(); }} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Bulk welcome — fires the magic-link to every imported staff who's
// never logged in yet (password_hash still the placeholder). Confirm
// dialog shows how many will go out.
function BulkWelcomeButton({ onSent }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm('Send the "set your password" magic link to every staff member who hasn\'t logged in yet?')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/staff/bulk-send-welcomes', { only_never_logged_in: true });
      toast.success(data.message);
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send');
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={send}
      disabled={busy}
      title="Send welcome emails to staff who haven't logged in yet"
      className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
    >
      <PaperAirplaneIcon className="w-4 h-4" />
      {busy ? 'Sending…' : 'Send welcome emails'}
    </button>
  );
}

// Staff card — wraps the existing link with a small "Send welcome" link
// for the director, shown only if the staff member hasn't set a password
// yet (placeholder marker = a missing handbook_acknowledged_at AND missing
// offer_letter_signed_at on the row).
function StaffCard({ s, isDirector, onSent }) {
  const [busy, setBusy] = useState(false);
  const neverLoggedIn =
    !s.handbook_acknowledged_at && !s.offer_letter_signed_at &&
    (s.status === 'active' || s.status === 'onboarding' || s.status === 'pending_hire' || s.status === 'pending_clearance');

  const sendOne = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Send welcome email to ${s.first_name} ${s.last_name} (${s.email})?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/${s.id}/send-welcome`);
      toast.success(data.message);
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <Link to={`/staff/${s.id}`}
        className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md hover:border-orange-300 transition-all block">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-sm font-bold text-orange-600">
            {s.first_name?.[0]}{s.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 truncate">{s.first_name} {s.last_name}</p>
            <p className="text-xs text-gray-500 truncate">{s.email}</p>
            {s.phone && (
              <p className="text-xs text-gray-500 truncate">{s.phone}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={s.role} />
          <PipelineStage stage={s.pipeline_stage} />
        </div>
        {s.hire_date && (
          <p className="text-xs text-gray-500 mt-2">Hired: {formatDateOnlyNumeric(s.hire_date)}</p>
        )}
      </Link>
      {isDirector && neverLoggedIn && (
        <button
          onClick={sendOne}
          disabled={busy}
          className="absolute top-3 right-3 text-xs text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100 px-2 py-1 rounded-md font-medium inline-flex items-center gap-1"
          title="Email this staff member a magic-link to set their password"
        >
          <PaperAirplaneIcon className="w-3.5 h-3.5" />
          {busy ? 'Sending…' : 'Send welcome'}
        </button>
      )}
    </div>
  );
}
